import {
  chipGroupFeatures,
  defaultOnboardingFeatures,
  landingPageReferralUrls,
  onboardingFeatures,
  termToFeatureMap,
  utmContentRemoteAssist,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { experiments } from 'client/GrowthEngineering/OptimizelyApi';
import { get } from 'lodash';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { mapActions, mapGetters, mapState } from 'vuex';
import ImplementationChecklistLoadingModal from 'client/components/ArtificialWait/ImplementationChecklistLoadingModal.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';

const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';
const orgCollection = 'OrganizationsCollection';
const settingsModule = 'SettingsModule';

const { createAdminAccount } = defaultOnboardingFeatures;
const defaultChecklistFeature = implementationChecklists[createAdminAccount];

export default {
  ModalEventBus,

  computed: {
    ...mapGetters(onboardingModule, {
      checklists: 'getImplementationChecklist',
      expandedIndex: 'getChecklistExpandedIndex',
      total: 'getImplementationChecklistTotal',
      totalComplete: 'getImplementationChecklistTotalComplete',
    }),

    ...mapGetters(orgCollection, {
      getItemDetails: 'getItemDetails',
    }),

    ...mapState(onboardingModule, [
      'adminGrowthData',
      'initalCheckListFeatures',
    ]),

    ...mapState(growthModule, [
      'orgGrowthData',
    ]),

    ...mapState(settingsModule, ['orgId']),

    progressBarTotal() {
      if (!this.checklists.length) {
        // Return default number instead of 0 to show initial progress and not an empty bar
        return 10;
      }
      return this.total;
    },

    percentage() {
      return Math.floor((this.totalComplete / this.total) * 100);
    },

    recommendedChecklists() {
      return Object.values(this.adminGrowthData.onboardingState.recommendedChecklists);
    },

    redirectUrl() {
      return '#/discover';
    },
  },

  methods: {
    ...mapActions(onboardingModule, [
      'addImplementationChecklists',
      'removeFeatureFromImplementationChecklist',
      'updateChecklistExpandedIndex',
      'updateImplementationChecklist',
    ]),

    checkExistingChecklists() {
      if (this.checklists.length) {
        this.checklists.forEach((checklist) => {
          if (!checklist.headerText) {
            // update older checklists with correctly formatted values to match features
            const existingFeature = chipGroupFeatures
              .find(feature => feature.name === checklist.feature);
            const defaultFeature = defaultOnboardingFeatures.createAdminAccount;
            const isDefaultFeature = checklist.feature === defaultFeature;

            if (existingFeature) {
              this.updateImplementationChecklist(existingFeature);
            } else if (isDefaultFeature) {
              this.updateImplementationChecklist({
                name: defaultOnboardingFeatures.createAdminAccount,
                value: defaultOnboardingFeatures.createAdminAccount,
              });
            } else {
              // Remove outdated checklist features
              this.removeFeatureFromImplementationChecklist(checklist);
            }
          }
        });
      }
    },

    directFeatureOnboarding(feature) {
      let directOnboarding = false;
      if (feature === onboardingFeatures.remoteAssist) {
        // If feature flag 2022-10-13-remote-assist-onboarding and
        // Check UTM field for 'Remote-Assist', skip onboarding
        const registrationUtmFields = get(this.getItemDetails(this.orgId), 'utmFields', {});
        const remoteAssistFlag = this.$hasFeatureFlag(this.$featureFlags.remoteAssistOnboarding);

        directOnboarding = registrationUtmFields
          && registrationUtmFields.utmContent === utmContentRemoteAssist
          && remoteAssistFlag;
      }
      return directOnboarding;
    },

    getLandingPageOnboardingFeature() {
      let landingPageOnboardingFeature = onboardingFeatures.deviceManagement;
      const registrationUtmFields = get(this.getItemDetails(this.orgId), 'utmFields', {});
      const utmTerm = get(registrationUtmFields, 'utmTerm');
      const growthExperimentStates = get(this.orgGrowthData, 'experimentStates', {});
      const { experimentId } = experiments.landingPageReferralOnboarding;
      const state = get(growthExperimentStates, experimentId, {});
      const signupReferralUrl = get(state, 'signupReferralUrl', '');

      // If the admin came from Cloud Directory, check utm_term for indication of desired feature
      if (signupReferralUrl.startsWith(landingPageReferralUrls.cloudDirectory)) {
        // Cloud Directory is our main branded search Landing Page
        // A lot of traffic here is someone searching for jumpcloud directly
        // We want to do some additional parsing to see if there is another search term for guidance
        // Do additional parsing to find feature based on additional search terms in first touch
        const splitTerm = (utmTerm ?? '').split(' ');
        const filteredTerms = splitTerm
          .filter(item => !!item.trim() && !item.includes('jump') && !item.includes('cloud'))
          .map(item => {
            const removeNonAlphaCharacters = item.replace(/[^a-zA-Z]/gmi, '');
            return removeNonAlphaCharacters.toLowerCase();
          });

        const term = filteredTerms.join('-');

        landingPageOnboardingFeature = termToFeatureMap[term] || onboardingFeatures.userManagement;
      }

      return landingPageOnboardingFeature;
    },

    filterFeatures(features) {
      let availableFeatures = features;
      if (!this.$hasFeatureFlag(this.$featureFlags.passwordManager)) {
        availableFeatures = availableFeatures
          .filter((feature) => feature.value !== onboardingFeatures.passwordManagement);
      }
      if (!this.$hasFeatureFlag(this.$featureFlags.remoteAssistOnboarding)) {
        availableFeatures = availableFeatures
          .filter((feature) => feature.value !== onboardingFeatures.remoteAssist);
      }
      return availableFeatures;
    },

    shouldStartCollapsed(checklist, index) {
      return checklist.newFeature ? true : index !== this.expandedIndex;
    },

    initializeImplementationChecklists(features) {
      // initialize with default feature, then add selected features
      const checklists = [defaultChecklistFeature];
      features.forEach((feature) => {
        checklists.push(implementationChecklists[feature.value]);
      });
      this.addImplementationChecklists(checklists);
    },

    openChecklistLoadingModal() {
      this.$options.ModalEventBus.open({
        component: ImplementationChecklistLoadingModal,
        componentProps: {
          featureList: this.initalCheckListFeatures,
        },
        isLargeModal: true,
        preventCloseOnOverlayClick: true,
      });
    },
  },

};
